import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import PageHeader from '../../common/PageHeader'
import Section, { SubSection } from '../../common/Section'
import Alert from '../../common/Alert'
import { SimpleCodeBlock, InlineCode } from '../../common/CodeBlock'
import Table from '../../common/Table'
import { useLocale } from '../../../i18n/useLocale'

export default function SandboxEnvironment() {
  const { urls, brand, support } = useLocale()

  return (
    <Box>
      <PageHeader
        title="Sandbox Environment"
        description="Test your integration in a safe sandbox environment before going live."
        breadcrumbs={[
          { label: 'Testing', path: '/testing' },
          { label: 'Sandbox Environment' },
        ]}
      />

      <Section title="Environment URLs" divider={false}>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {brand.name} provides separate environments for testing and production:
        </Typography>

        <Table
          columns={[
            { key: 'env', header: 'Environment', width: '20%' },
            { key: 'url', header: 'Base URL' },
            { key: 'purpose', header: 'Purpose', width: '30%' },
          ]}
          data={[
            {
              env: <Chip label="Sandbox" color="warning" size="small" />,
              url: <InlineCode>{urls.sandboxBase}</InlineCode>,
              purpose: 'Development and testing',
            },
            {
              env: <Chip label="Production" color="success" size="small" />,
              url: <InlineCode>{urls.apiBase}</InlineCode>,
              purpose: 'Live transactions',
            },
          ]}
        />

        <Alert type="warning" title="Separate Credentials">
          Sandbox and production environments use different API keys. Request sandbox credentials from your account manager.
        </Alert>
      </Section>

      <Section title="Sandbox Features">
        <Typography variant="body1" sx={{ mb: 2 }}>
          The sandbox environment provides:
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Full API Access</Typography>
              <Typography variant="body2" color="text.secondary">
                All API endpoints available with test data. Create parcels, generate labels, and simulate payments.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Test APM Locations</Typography>
              <Typography variant="body2" color="text.secondary">
                Sandbox includes test APM locations for development. Use these for destination testing.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>M-Pesa Sandbox</Typography>
              <Typography variant="body2" color="text.secondary">
                Simulated M-Pesa payments without real money. Test STK Push flows safely.
              </Typography>
            </CardContent>
          </Card>
          <Card variant="outlined" sx={{ flex: '1 1 300px' }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>No Rate Limits</Typography>
              <Typography variant="body2" color="text.secondary">
                Relaxed rate limits for development. Test batch operations freely.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Section>

      <Section title="Test Credentials">
        <SubSection title="Sample API Key">
          <SimpleCodeBlock
            code={`API Key: sk_test_sample123456789`}
          />
          <Alert type="info">
            Request your actual sandbox credentials from <strong>{support.salesEmail}</strong>
          </Alert>
        </SubSection>

        <SubSection title="Test Phone Numbers">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Use these phone numbers for M-Pesa sandbox testing:
          </Typography>
          <Table
            columns={[
              { key: 'phone', header: 'Phone Number' },
              { key: 'behavior', header: 'Behavior' },
            ]}
            data={[
              { phone: <InlineCode>+254700000001</InlineCode>, behavior: 'Payment succeeds' },
              { phone: <InlineCode>+254700000002</InlineCode>, behavior: 'Payment fails (insufficient funds)' },
              { phone: <InlineCode>+254700000003</InlineCode>, behavior: 'Payment timeout (no response)' },
              { phone: <InlineCode>+254700000004</InlineCode>, behavior: 'User cancels' },
            ]}
          />
        </SubSection>

        <SubSection title="Test APM IDs">
          <Typography variant="body2" sx={{ mb: 2 }}>
            Sandbox APM locations for testing:
          </Typography>
          <Table
            columns={[
              { key: 'id', header: 'APM ID' },
              { key: 'name', header: 'Name' },
              { key: 'location', header: 'Location' },
            ]}
            data={[
              { id: <InlineCode>test001</InlineCode>, name: 'Test APM - Nairobi CBD', location: 'Nairobi' },
              { id: <InlineCode>test002</InlineCode>, name: 'Test APM - Westlands', location: 'Nairobi' },
              { id: <InlineCode>test003</InlineCode>, name: 'Test APM - Mombasa', location: 'Mombasa' },
              { id: <InlineCode>test004</InlineCode>, name: 'Test APM - Kisumu', location: 'Kisumu' },
            ]}
          />
        </SubSection>
      </Section>

      <Section title="Testing Checklist">
        <Typography variant="body1" sx={{ mb: 2 }}>
          Before going live, verify these integration scenarios:
        </Typography>

        <Box component="ol" sx={{ pl: 3, '& li': { mb: 2 } }}>
          <li>
            <strong>Authentication</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>API key authentication works correctly</li>
              <li>Error responses for invalid credentials are handled</li>
            </Box>
          </li>
          <li>
            <strong>Parcel Creation</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>Single parcel creation via JSON</li>
              <li>Batch parcel creation via file upload</li>
              <li>Validation errors are handled and displayed</li>
              <li>Successful parcels return valid parcel numbers</li>
            </Box>
          </li>
          <li>
            <strong>Label Generation</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>PDF labels download correctly</li>
              <li>ZPL labels work with thermal printers</li>
              <li>Batch label generation works</li>
            </Box>
          </li>
          <li>
            <strong>Tracking</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>Single parcel status queries work</li>
              <li>Batch status queries work</li>
              <li>Status updates are reflected correctly</li>
            </Box>
          </li>
          <li>
            <strong>Payments (if applicable)</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>M-Pesa STK Push initiates correctly</li>
              <li>Payment status queries return correct state</li>
              <li>Failed payments are handled gracefully</li>
            </Box>
          </li>
          <li>
            <strong>APM Finder Widget</strong>
            <Box component="ul" sx={{ mt: 1, pl: 2 }}>
              <li>Widget loads in iframe</li>
              <li>Selection events are received</li>
              <li>Selected APM ID works with parcel creation</li>
            </Box>
          </li>
        </Box>
      </Section>

      <Section title="Going Live">
        <Typography variant="body1" sx={{ mb: 2 }}>
          When you're ready to go live:
        </Typography>

        <Box component="ol" sx={{ pl: 3, '& li': { mb: 1.5 } }}>
          <li>Complete all checklist items above</li>
          <li>Request production API credentials from your account manager</li>
          <li>Update your configuration to use production URLs</li>
          <li>Replace sandbox API keys with production keys</li>
          <li>Test with a small number of real transactions</li>
          <li>Monitor for errors in your logging system</li>
          <li>Scale up as confidence grows</li>
        </Box>

        <Alert type="success" title="Need Help?">
          Contact <strong>{support.salesEmail}</strong> for assistance with your integration or production setup.
        </Alert>
      </Section>
    </Box>
  )
}
